import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChefHat, Clock, Award, ArrowRight, MapPin, Home as HomeIcon, Users } from "lucide-react";
import axios from "axios";
import ElegantPlaceholder from "@/components/ElegantPlaceholder";
import StockImage from "@/components/StockImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">
    {children}
  </p>
);

function ReviewCard({ r, idx }) {
  const [offen, setOffen] = useState(false);
  const lang = (r.text || "").length > 190;
  return (
    <figure className="h-full border border-[#E3DECE] bg-[#FAFAF7] p-8 flex flex-col" data-testid={`testimonial-card-${idx}`}>
      <div className="text-[#BA6D52] tracking-[0.3em] text-sm" aria-label={`${r.rating} von 5 Sternen`}>
        {"★".repeat(r.rating)}
        <span className="text-[#E3DECE]">{"★".repeat(Math.max(0, 5 - r.rating))}</span>
      </div>
      {r.text && (
        <blockquote
          className={`mt-5 text-sm font-light leading-relaxed text-[#2C2A26] flex-1 ${
            lang && !offen ? "line-clamp-5" : ""}`}>
          „{r.text}“
        </blockquote>
      )}
      {lang && (
        <button onClick={() => setOffen((o) => !o)} data-testid={`testimonial-more-${idx}`}
          className="mt-4 mb-6 self-start text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] hover:text-[#9E5841] transition-colors">
          {offen ? "Weniger anzeigen" : "Mehr lesen"}
        </button>
      )}
      <figcaption className="mt-auto pt-4 border-t border-[#E3DECE] text-xs uppercase tracking-[0.2em] text-[#757067] flex items-baseline justify-between gap-3">
        <span>{r.betrieb}</span>
        {r.quelle === "google" && (
          <span className="text-[10px] tracking-[0.15em] text-[#9A948A] normal-case">Google</span>
        )}
      </figcaption>
    </figure>
  );
}

function Testimonials({ reviews, googleUrl }) {
  if (!reviews.length) return null;
  return (
    <section id="bewertungen" className="border-b border-[#E3DECE] bg-[#F2EFE9] scroll-mt-24" data-testid="home-testimonials">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Overline>Referenzen</Overline>
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tighter mt-6 text-[#2C2A26]">
          Was Kunden sagen
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {reviews.slice(0, 6).map((r, idx) => <ReviewCard key={idx} r={r} idx={idx} />)}
        </div>
        {googleUrl && (
          <p className="mt-10 text-sm font-light text-[#757067]">
            <a href={googleUrl} target="_blank" rel="noreferrer" data-testid="home-google-reviews-link"
              className="text-[#BA6D52] hover:text-[#9E5841] underline decoration-[#E3DECE] hover:decoration-[#BA6D52] underline-offset-4 transition-colors">
              Alle Bewertungen auf Google ansehen
            </a>
          </p>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [googleUrl, setGoogleUrl] = useState("");
  useEffect(() => {
    axios.get(`${API}/reviews/public`).then((r) => {
      setReviews(r.data?.reviews || []);
      setGoogleUrl(r.data?.google_url || "");
    }).catch(() => {});
  }, []);
  const avgRating = reviews.length ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length : 0;

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 fade-in">
            <Overline>Mietkoch · Privatkoch · Koch-Vermittlung</Overline>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] mt-6 text-[#2C2A26]">
              Lukas Stutz
            </h1>
            {/* Mobile: kurzer Text */}
            <p className="mt-6 md:hidden text-base font-light leading-relaxed text-[#757067] max-w-xl">
              Ausgebildeter Koch für Gastronomiebetriebe, Hotels und Firmenevents,
              <b className="text-[#2C2A26] font-medium"> Privatkoch für Menü-Abende bei Ihnen zuhause</b> —
              und ich vermittle selbstständige Mietköche und Privatköche an Betriebe und Privatkunden.
            </p>
            {/* Desktop / Tablet: ausführlicher */}
            <p className="mt-8 hidden md:block text-base md:text-lg font-light leading-relaxed text-[#757067] max-w-xl">
              Ausgebildeter Koch im flexiblen Einsatz für Gastronomiebetriebe,
              Hotels, Restaurants und Firmenevents — als
              <b className="text-[#2C2A26] font-medium"> Privatkoch für Menü-Abende bei Ihnen zuhause</b> von
              Fine-Dining bis veganem Menü — und als
              <b className="text-[#2C2A26] font-medium"> Vermittler selbstständiger Mietköche und Privatköche</b> —
              für Betriebe, die kurzfristig Verstärkung brauchen, und für Privatkunden, die einen Koch
              für ihren Anlass suchen.
            </p>
            <div className="mt-8 md:mt-12 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              <Link
                to="/anfrage"
                data-testid="hero-cta-anfrage"
                className="inline-flex items-center justify-center gap-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-6 md:px-8 py-4 md:py-5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Anfrage stellen <ArrowRight size={16} />
              </Link>
              <Link
                to="/privatkoch"
                data-testid="hero-cta-privatkoch"
                className="inline-flex items-center justify-center gap-3 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white px-6 md:px-8 py-4 md:py-5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Privatkoch für Zuhause
              </Link>
              <Link
                to="/leistungen"
                data-testid="hero-cta-leistungen"
                className="hidden sm:inline-flex items-center justify-center gap-3 border border-[#E3DECE] text-[#2C2A26] hover:border-[#BA6D52] hover:text-[#BA6D52] px-6 md:px-8 py-4 md:py-5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Leistungen
              </Link>
            </div>
            <p className="mt-5 text-sm text-[#757067] font-light">
              Sie brauchen kurzfristig einen Koch oder möchten selbst vermittelt werden?{" "}
              <Link
                to="/vermittlung"
                data-testid="hero-link-vermittlung"
                className="text-[#BA6D52] hover:text-[#9E5841] underline decoration-[#E3DECE] hover:decoration-[#BA6D52] underline-offset-4 transition-colors"
              >
                Zur Koch-Vermittlung
              </Link>
            </p>
            {reviews.length > 0 && (
              <button
                type="button"
                onClick={() => document.getElementById("bewertungen")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                aria-label="Zu den Bewertungen springen"
                className="group mt-8 flex items-center gap-3 flex-wrap text-left cursor-pointer"
                data-testid="hero-rating-badge"
              >
                <span className="text-[#BA6D52] tracking-[0.2em] text-lg leading-none">
                  {"★".repeat(Math.round(avgRating))}
                  <span className="text-[#E3DECE]">{"★".repeat(Math.max(0, 5 - Math.round(avgRating)))}</span>
                </span>
                <span className="text-sm text-[#2C2A26] font-medium">{avgRating.toFixed(1).replace(".", ",")}</span>
                <span className="text-sm text-[#757067] font-light">
                  · {reviews.length} Bewertung{reviews.length > 1 ? "en" : ""}
                  {reviews.some((r) => r.quelle === "google") ? " · Google & Betriebe" : " von Gastronomiebetrieben"}
                </span>
                <span className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] underline decoration-[#E3DECE] group-hover:decoration-[#BA6D52] underline-offset-4 transition-colors">
                  Ansehen
                </span>
              </button>
            )}
          </div>
          <div className="lg:col-span-6 fade-in delay-2">
            <div className="relative">
              {/* feiner, versetzter Rahmen als ruhiger Akzent */}
              <span aria-hidden className="hidden sm:block absolute -top-5 -right-5 w-24 h-24 border-t border-r border-[#BA6D52]/60" />
              <span aria-hidden className="hidden sm:block absolute -bottom-5 -left-5 w-24 h-24 border-b border-l border-[#BA6D52]/60" />
              <div className="group relative w-full aspect-[4/5] overflow-hidden border border-[#E3DECE] bg-[#F2EFE9] shadow-[0_30px_60px_-20px_rgba(44,42,38,0.35)]">
                <img
                  src="/hero-portrait-4x5.jpg"
                  alt="Lukas Stutz — Mietkoch für Gastronomie & Events"
                  className="w-full h-full object-cover [object-position:50%_30%] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  data-testid="home-hero-portrait"
                />
                {/* sanfte Tiefe nach unten, damit die Bildunterschrift ruhig sitzt */}
                <span aria-hidden className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#2C2A26]/90 via-[#2C2A26]/35 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 px-6 py-5 flex items-end justify-between gap-4">
                  <span className="text-[10px] uppercase tracking-[0.25em] font-medium text-[#F2EFE9]">
                    Gelernter Koch · selbst am Herd
                  </span>
                  <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#F2EFE9]/75">
                    Michelstadt · DE &amp; AT
                  </span>
                </figcaption>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DREI WEGE */}
      <section className="border-b border-[#E3DECE] bg-[#2C2A26] text-[#F2EFE9]" data-testid="home-drei-wege">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D69173]">Drei Wege</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mt-6 leading-[1.1]">
              Ein Koch, drei Arten<br className="hidden sm:block" /> zusammenzuarbeiten.
            </h2>
            <p className="mt-6 text-[#B3ADA2] font-light leading-relaxed max-w-xl">
              Wählen Sie, was zu Ihnen passt — ich melde mich persönlich, meist innerhalb
              von 24 Stunden.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#403C36]">
            {[
              {
                nr: "01",
                icon: ChefHat,
                title: "Mietkoch für Betriebe",
                text: "Ich stehe selbst bei Ihnen am Herd — à la carte, Bankett, Frühstück oder Catering. Stundenweise, kurzfristig, ohne Einarbeitungsverlust.",
                meta: "ab 49 € netto / Std · B2B",
                to: "/anfrage-betrieb",
                cta: "Einsatz anfragen",
                testid: "weg-mietkoch",
              },
              {
                nr: "02",
                icon: HomeIcon,
                title: "Privatkoch für Zuhause",
                text: "Ihr Menü-Abend in Ihrer eigenen Küche: Einkauf, Vorbereitung, Service und eine aufgeräumte Küche zum Abschied. Ab 2 Gästen — größere Runden auf Anfrage.",
                meta: "Festpreis pro Person · privat",
                to: "/privatkoch",
                cta: "Private Dining entdecken",
                testid: "weg-privatkoch",
              },
              {
                nr: "03",
                icon: Users,
                title: "Koch-Vermittlung",
                text: "Wenn ich selbst keine Zeit habe: Ich vermittle geprüfte selbstständige Köche aus meinem Netzwerk — an Betriebe und an Privatkunden.",
                meta: "Betriebe · Privatkunden · Festanstellung",
                to: "/vermittlung",
                cta: "Vermittlung ansehen",
                testid: "weg-vermittlung",
              },
            ].map((w) => (
              <Link
                key={w.nr}
                to={w.to}
                data-testid={w.testid}
                className="group relative bg-[#2C2A26] hover:bg-[#33302A] p-8 md:p-10 flex flex-col transition-colors duration-300"
              >
                <div className="flex items-start justify-between">
                  <w.icon size={30} strokeWidth={1.15} className="text-[#D69173] transition-transform duration-500 group-hover:-translate-y-1" />
                  <span className="font-serif text-4xl font-light text-[#4A453D] group-hover:text-[#BA6D52] transition-colors duration-300">
                    {w.nr}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-[1.75rem] font-light mt-8 leading-snug">
                  {w.title}
                </h3>
                <p className="mt-4 text-sm text-[#B3ADA2] font-light leading-relaxed flex-1">
                  {w.text}
                </p>
                <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-[#8A8378]">{w.meta}</p>
                <span className="mt-6 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[#D69173]">
                  {w.cta}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
                <span className="absolute left-0 bottom-0 h-px w-0 bg-[#BA6D52] transition-all duration-500 group-hover:w-full" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Vermittlung: drei Wege, bewusst ruhig gehalten */}
      <section className="border-b border-[#E3DECE] bg-[#FAFAF7]" data-testid="home-vermittlung">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="md:flex md:items-end md:justify-between md:gap-16">
            <div className="max-w-2xl">
              <Overline>Koch-Vermittlung</Overline>
              <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tighter mt-6 text-[#2C2A26] leading-[1.15]">
                Und wenn ich selbst<br className="hidden sm:block" /> keine Zeit habe?
              </h2>
              <p className="mt-6 text-[#757067] font-light leading-relaxed">
                Dann bleibt Ihre Küche trotzdem besetzt. Ich vermittle geprüfte selbstständige Köche
                aus meinem Netzwerk — an Betriebe, an Privatkunden und auf Wunsch in Festanstellung.
                In Deutschland und Österreich.
              </p>
            </div>
            <Link to="/vermittlung" data-testid="home-vermittlung-alle"
              className="hidden md:inline-flex items-center gap-3 shrink-0 text-[11px] uppercase tracking-[0.2em] text-[#BA6D52] group">
              Alles zur Vermittlung
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>

          <div className="mt-16 border-t border-[#E3DECE]">
            {[
              {
                nr: "I",
                title: "Als Mietkoch oder Privatkoch registrieren",
                text: "Für selbstständige Köche: Einsätze in Betrieben, Private Dining oder beides. Dauerhaft kostenlos, ohne Bindung.",
                to: "/mietkoch-werden",
                testid: "home-vermittlung-koch",
              },
              {
                nr: "II",
                title: "Mietkoch anfordern",
                text: "Für Gastronomie und Hotellerie: geprüfte Köche bei Ausfall, Saisonspitze oder offener Stelle. Rückmeldung in 24 Stunden.",
                to: "/vermittlung/koch-anfordern",
                testid: "home-vermittlung-betrieb",
              },
              {
                nr: "III",
                title: "Privatkoch anfragen",
                text: "Für Privatkunden und Familien: ein passender Koch für Ihren Anlass, der bei Ihnen zu Hause kocht.",
                to: "/vermittlung/privatkoch-anfragen",
                testid: "home-vermittlung-privat",
              },
            ].map((w) => (
              <Link key={w.nr} to={w.to} data-testid={w.testid}
                className="group relative flex flex-col md:flex-row md:items-baseline gap-2 md:gap-10 border-b border-[#E3DECE] py-7 md:py-8 transition-colors duration-300 hover:bg-white">
                <span className="font-serif text-sm text-[#BA6D52] w-8 shrink-0 md:pl-1">{w.nr}</span>
                <span className="md:w-[22rem] shrink-0">
                  <span className="font-serif text-xl md:text-[1.4rem] font-light text-[#2C2A26] leading-snug">
                    {w.title}
                  </span>
                </span>
                <span className="text-sm text-[#757067] font-light leading-relaxed md:flex-1">{w.text}</span>
                <ArrowRight size={16}
                  className="shrink-0 text-[#BA6D52] opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 self-start md:self-auto" />
              </Link>
            ))}
          </div>

          <Link to="/vermittlung" data-testid="home-vermittlung-alle-mobil"
            className="md:hidden inline-flex items-center gap-3 mt-10 text-[11px] uppercase tracking-[0.2em] text-[#BA6D52]">
            Alles zur Vermittlung <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* USP */}
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            {
              icon: ChefHat,
              title: "Ausgebildeter Koch",
              text: "Solide handwerkliche Ausbildung, Erfahrung in unterschiedlichen Küchenstilen und Konzepten.",
              image: "/images/ausgebildeter-koch.jpg",
            },
            {
              icon: Clock,
              title: "Flexibel & verlässlich",
              text: "Kurzfristige Einsätze, Wochenenden, Feiertage — abgestimmt auf Ihren Betriebsablauf.",
              image: "https://images.unsplash.com/photo-1592861956120-e524fc739696?w=1200&q=80&auto=format&fit=crop",
            },
            {
              icon: Award,
              title: "Qualität ohne Kompromisse",
              text: "Strukturierte Mise en Place, ruhige Linie am Pass, saubere Übergabe.",
              image: "/images/qualitaet.jpg",
            },
          ].map((u, i) => (
            <div key={i} className="fade-in flex flex-col" style={{ animationDelay: `${0.1 * i}s` }}>
              <div className="relative w-full aspect-[4/3] overflow-hidden border border-[#E3DECE] bg-[#F2EFE9] mb-6">
                <img src={u.image} alt={u.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <u.icon className="text-[#BA6D52]" size={28} strokeWidth={1.25} />
              <h3 className="font-serif text-lg sm:text-xl lg:text-2xl mt-4 font-medium text-[#2C2A26] leading-tight">
                {u.title}
              </h3>
              <p className="mt-3 text-[#757067] font-light leading-relaxed">
                {u.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* LEISTUNGEN TEASER — Editorial Bento */}
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-2xl">
            <Overline>Einsatzbereiche</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mt-6 text-[#2C2A26]">
              Vom À-la-carte-Geschäft bis zum Catering.
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-7">
              <StockImage
                aspect="aspect-[16/10]"
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&q=80&auto=format&fit=crop"
                alt="Restaurant-Service"
              />
              <div className="mt-6">
                <h3 className="font-serif text-2xl">Gastronomie & Restaurants</h3>
                <p className="text-[#757067] font-light mt-2">
                  Aushilfe an Mittag- oder Abend-Service, à la carte und Bankett.
                </p>
              </div>
            </div>
            <div className="md:col-span-5">
              <StockImage
                aspect="aspect-[4/5]"
                src="https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1200&q=80&auto=format&fit=crop"
                alt="Hotellerie"
              />
              <div className="mt-6">
                <h3 className="font-serif text-2xl">Hotellerie</h3>
                <p className="text-[#757067] font-light mt-2">
                  Frühstück, Halbpension, Bankette — Saisonspitzen souverän abgedeckt.
                </p>
              </div>
            </div>
            <div className="md:col-span-5">
              <StockImage
                aspect="aspect-[4/5]"
                src="https://images.unsplash.com/photo-1482275548304-a58859dc31b7?w=1200&q=80&auto=format&fit=crop"
                alt="Firmenevent"
              />
              <div className="mt-6">
                <h3 className="font-serif text-2xl">Firmenevents & Bankette</h3>
                <p className="text-[#757067] font-light mt-2">
                  Unterstützung bei Geschäftsessen, Firmenfeiern und Banketten in
                  bestehenden Gastro-Betrieben.
                </p>
              </div>
            </div>
            <div className="md:col-span-7">
              <StockImage
                aspect="aspect-[16/10]"
                src="https://images.unsplash.com/photo-1555244162-803834f70033?w=1600&q=80&auto=format&fit=crop"
                alt="Catering-Buffet"
              />
              <div className="mt-6">
                <h3 className="font-serif text-2xl">Catering-Support</h3>
                <p className="text-[#757067] font-light mt-2">
                  Verstärkung Ihres Catering-Teams bei Großveranstaltungen.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16">
            <Link
              to="/leistungen"
              data-testid="home-link-leistungen"
              className="inline-flex items-center gap-3 border border-[#E3DECE] text-[#2C2A26] hover:border-[#BA6D52] hover:text-[#BA6D52] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Alle Leistungen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-6">
            <Overline>Richtpreis</Overline>
            <div className="mt-8 flex items-baseline gap-x-5 flex-wrap">
              <span className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tighter text-[#2C2A26]">
                49&nbsp;€
              </span>
              <span className="text-base sm:text-lg text-[#757067] font-light tracking-wide">
                / Std netto · Werktag
              </span>
            </div>
            <p className="mt-6 text-sm text-[#757067] font-light leading-relaxed">
              An Sonn- &amp; Feiertagen <span className="text-[#BA6D52] font-medium">59 €</span> netto / Std
            </p>
          </div>
          <div className="md:col-span-6 space-y-6">
            <div className="border-t border-[#E3DECE] pt-6 flex justify-between items-baseline">
              <span className="text-[#2C2A26]">Mindestbuchung</span>
              <span className="font-serif text-2xl">4 Stunden</span>
            </div>
            <div className="border-t border-[#E3DECE] pt-6 flex justify-between items-baseline">
              <span className="text-[#2C2A26]">Spontanbuchung (&lt; 24 h)</span>
              <span className="font-serif text-2xl">+ 10 %</span>
            </div>
            <div className="pt-4">
              <Link
                to="/preise"
                data-testid="home-link-preise"
                className="inline-flex items-center gap-3 text-[#BA6D52] hover:text-[#9E5841] text-xs uppercase tracking-[0.2em]"
              >
                Preise im Detail <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials reviews={reviews} googleUrl={googleUrl} />

      {/* CTA FINAL */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <Overline>Bereit für die nächste Saison?</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 max-w-3xl mx-auto">
            Buchen Sie mich für Ihren Einsatz.
          </h2>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/anfrage"
              data-testid="final-cta-anfrage"
              className="inline-flex items-center gap-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Anfrage stellen <ArrowRight size={16} />
            </Link>
            <Link
              to="/kontakt"
              data-testid="final-cta-kontakt"
              className="inline-flex items-center gap-3 border border-[#E3DECE] text-[#2C2A26] hover:border-[#BA6D52] hover:text-[#BA6D52] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <MapPin size={16} /> Kontakt
            </Link>
            <Link
              to="/vermittlung"
              data-testid="final-cta-vermittlung"
              className="inline-flex items-center gap-3 border border-[#E3DECE] text-[#2C2A26] hover:border-[#BA6D52] hover:text-[#BA6D52] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <Users size={16} /> Koch-Vermittlung
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
